import { Box, Button, Paper, Stack, styled, Typography } from '@mui/material';
import Layout from '@src/components/layout/Layout';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { RegionLookUps } from '@src/constants/Regions';
import SearchContainer from './components/SearchContainer';
import useSearchHospitalsLazy from '@src/adapters/hooks/hospital/useSearchHospitalsLazy';
import { Routes } from '@src/navigation/Routes';
import ActionsButtonMenu from '@src/components/common/buttons/ActionButtonMenu';
import HospitalsTable from './components/HospitalsTable';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';
import useOrganisationDeactivateLazy from '@src/adapters/hooks/organisation/useOrganisationDeactivate';
import { useSnackbar } from 'notistack';
import useOrganisationActivateLazy from '@src/adapters/hooks/organisation/useOrganisationActivateLazy';
import OrganisationHospitalDTO from '@src/adapters/dto/hospital/OrganisationHospitalDTO';

const PREFIX = 'Hospitals';

const classes = {
  titleBox: `${PREFIX}-pageTitle`,
};

const StyledLayout = styled(Layout)(({ theme }) => ({
  [`& .${classes.titleBox}`]: {
    marginBottom: '33px',
  },
}));

type HospitalUsersFilters = {
  isActive: boolean;
  searchQuery: string;
  regionCode: string;
};

const DEFAULT_FILTERS: HospitalUsersFilters = {
  isActive: true,
  regionCode: '',
  searchQuery: '',
};

function Hospitals() {
  const { currentOrganisation } = useNavigationStore();
  const { execute: searchHospitalsList } = useSearchHospitalsLazy();
  const { execute: deactivateOrganisation } = useOrganisationDeactivateLazy();
  const { execute: activateOrganisation } = useOrganisationActivateLazy();
  const [hospitals, setHospitals] = useState<OrganisationHospitalDTO[]>([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setCurrentPage(0);
  };

  const findHospitals = useCallback(async () => {
    try {
      const result = await searchHospitalsList({
        pageSize: rowsPerPage,
        currentPage: currentPage,
        searchText: appliedFilters.searchQuery,
        regionCode: appliedFilters.regionCode,
        isActive: appliedFilters.isActive,
      });

      setHospitals(result.records);
      setRecordCount(result.recordCount);
    } catch (error) {
      console.error('Failed to fetch organisations:', error);
    }
  }, [
    searchHospitalsList,
    rowsPerPage,
    currentPage,
    appliedFilters.searchQuery,
    appliedFilters.regionCode,
    appliedFilters.isActive,
  ]);

  useEffect(() => {
    findHospitals();
  }, [findHospitals]);

  return (
    <StyledLayout>
      <Box
        sx={{
          px: 2,
        }}>
        <Stack
          direction={'row'}
          justifyContent="space-between"
          className={classes.titleBox}>
          <Typography variant="h4">{currentOrganisation?.name}</Typography>

          <Stack direction={'row'} gap={2}>
            <Button
              component={Link}
              to={generatePath(Routes.OrganisationHospitalAdd, {
                organisationSlug: currentOrganisation?.name ?? '',
              })}
              variant="outlined"
              size="large"
              color="primary">
              Add New Hospital
            </Button>
            <ActionsButtonMenu
              items={[
                {
                  key: 'edit-organisation',
                  label: 'Edit organisation',
                  to: generatePath(Routes.OrganisationEdit, {
                    organisationSlug: currentOrganisation?.name ?? '',
                  }),
                },
                ...(currentOrganisation?.isActive
                  ? [
                      {
                        key: 'deactivate-organisation',
                        label: 'Deactivate organisation',
                        async onClick() {
                          await deactivateOrganisation({
                            organisationId: currentOrganisation?.id!,
                          });
                          enqueueSnackbar(`Hospital Deactivated`, {
                            variant: 'success',
                          });
                          navigate(-1);
                        },
                      },
                    ]
                  : [
                      {
                        key: 'activate-organisation',
                        label: 'Activate organisation',
                        async onClick() {
                          await activateOrganisation({
                            organisationId: currentOrganisation?.id!,
                          });
                          enqueueSnackbar(`Hospital Activated`, {
                            variant: 'success',
                          });
                          navigate(-1);
                        },
                      },
                    ]),
              ]}
            />
          </Stack>
        </Stack>

        <SearchContainer
          regions={RegionLookUps}
          setRegionCode={(value: string) =>
            setFilters((prev) => ({ ...prev, regionCode: value }))
          }
          regionCode={filters.regionCode}
          searchQuery={filters.searchQuery}
          setSearchQuery={(value: string) =>
            setFilters((prev) => ({ ...prev, searchQuery: value }))
          }
          isActive={filters.isActive}
          setStatus={(value: boolean) =>
            setFilters((prev) => ({ ...prev, isActive: value }))
          }
          onSearch={handleSearch}
          onClearFilters={handleClearFilters}
        />

        <Paper>
          <HospitalsTable
            hospitals={hospitals}
            selectedPage={currentPage}
            rowsPerPage={rowsPerPage}
            recordCount={recordCount}
            changePage={(value: number) => setCurrentPage(value)}
            changeRowsPerPage={(value: number) => setRowsPerPage(value)}
          />
        </Paper>
      </Box>
    </StyledLayout>
  );
}

export default Hospitals;
